
import React from 'react';
import { format } from 'date-fns';
import { Class, ClassSchedule } from '@/types/classTypes';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { X, Clock, User, Calendar as CalendarIcon } from 'lucide-react';
import ClassScheduleDisplay from '../../classes/ClassScheduleDisplay';

interface EventDetailsPanelProps {
  selectedEvent: {
    class: Class;
    schedules: ClassSchedule[];
  };
  onClose: () => void;
  onScheduleUpdated: () => Promise<void>;
  onScheduleDeleted: () => Promise<void>;
}

export const EventDetailsPanel: React.FC<EventDetailsPanelProps> = ({
  selectedEvent,
  onClose,
  onScheduleUpdated,
  onScheduleDeleted
}) => {
  const { class: classData, schedules } = selectedEvent;
  
  return (
    <Card className="h-full bg-white/70 backdrop-blur-sm border border-gray-100 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-lg font-medium">{classData.name}</CardTitle>
        <div className="flex items-center space-x-2">
          <Badge className={classData.is_event ? "bg-pink-100 text-pink-700" : "bg-blue-100 text-blue-700"}>
            {classData.is_event ? 'Event' : 'Class'}
          </Badge>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {classData.description && (
          <div className="text-sm text-slate-600 border-b pb-3">
            {classData.description}
          </div>
        )}
        
        <div className="space-y-2">
          {classData.location && (
            <div className="flex items-center text-sm">
              <CalendarIcon className="h-4 w-4 mr-2 text-slate-400" />
              <span>Location: {classData.location}</span>
            </div>
          )}
          
          {classData.max_students && (
            <div className="flex items-center text-sm">
              <User className="h-4 w-4 mr-2 text-slate-400" />
              <span>Capacity: {classData.max_students} students</span>
            </div>
          )}
          
          {schedules.length > 0 && (
            <div className="pt-2">
              <h4 className="text-sm font-medium mb-2 flex items-center">
                <Clock className="h-4 w-4 mr-2 text-slate-400" />
                Schedule
              </h4>
              <ClassScheduleDisplay 
                schedules={schedules}
                classItem={classData}
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
